import useUserBackgroundPull from "./useUserBackgroundPull";
import useAircraftBackgroundPull from "./useAircraftBackgroundPull";
import useBaseLocationBackgroundPull from "./useBaseLocationBackgroundPull";
import useCompanyBackgroundPull from "./useCompanyBackgroundPull";
import useAmeBackgroundPull from "./useAmeBackgroundPull";
import useLineItemTypeBackgroundPull from "./useLineItemTypeBackgroundPull";
import usePilotBackgroundPull from "./usePilotBackgroundPull";
import useQuantityTypeBackgroundPull from "./useQuantityTypeBackgroundPull";
import useRiskAssessmentFactorBackgroundPull from "./useRiskAssessmentFactorBackgroundPull";
import useSignableResourceTypeBackgroundPull from "./useSignableResourceTypeBackgroundPull";
import useJobBackgroundPull from "./useJobBackgroundPull";
import * as React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { CircularProgress, Dialog, Grid, useTheme } from "@mui/material";
import ProgressIcon from "@mui/icons-material/FiberManualRecord";
import SuccessIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Cancel";
import { SyncStatus } from "../Utils/Interfaces";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { HasValue } from "../sharedCode/HelperMethods";
import { deleteTicketsPastRetention, ResetSyncCounts, TicketDatabase } from "../Utils/TicketDatabase";
import WarningIcon from "@mui/icons-material/Help";
import Button from "@mui/material/Button";
import { APPLICATION_VERSION, DATE_FORMAT } from "../sharedCode/metadata";
import useDuplicateTicketResolver from "./useDuplicateTicketResolver";
import useTicketBackgroundPush from "./useTicketBackgroundPush";
import { GlobalContext } from "../Utils/Contexts";
import useStaleTicketBackgroundPush from "./useStaleTicketBackgroundPush";

interface Props {
    setStatus: (status: SyncStatus) => void;
}

enum FormType {
    none,
    sync,
    push,
}

export const DATE_CHANGE_TRACKER = "ACTIVATION_TIME";
export default function SyncManager(props: Props): JSX.Element {
    const [showForm, setShowForm] = useState(FormType.none);
    const useEffectAnchor = "TODO";
    const context = useContext(GlobalContext);
    const theme = useTheme();
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doUiUpdate, setDoUiUpdate] = useState(false);
    const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const getChangesTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const updateUi = () => {
        setDoUiUpdate(true);
    };

    // if you move ticket push in this array then you have to update the context.shouldPushTickets useEffect too
    const pullers = [
        useTicketBackgroundPush({ updateUi }),
        useUserBackgroundPull({ updateUi }),
        useJobBackgroundPull({ updateUi }),
        useAircraftBackgroundPull({ updateUi }),
        useAmeBackgroundPull({ updateUi }),
        useBaseLocationBackgroundPull({ updateUi }),
        useCompanyBackgroundPull({ updateUi }),
        useLineItemTypeBackgroundPull({ updateUi }),
        usePilotBackgroundPull({ updateUi }),
        useQuantityTypeBackgroundPull({ updateUi }),
        useRiskAssessmentFactorBackgroundPull({ updateUi }),
        useSignableResourceTypeBackgroundPull({ updateUi }),
        useDuplicateTicketResolver({ updateUi }),
        useStaleTicketBackgroundPush({ updateUi }),
    ];

    useEffect(() => {
        if (context.shouldPushTickets) {
            const syncAllTickets = async () => {
                await ResetSyncCounts();
                setStatus(SyncStatus.Loading);
                props.setStatus(SyncStatus.Loading);
                setShowForm(FormType.push);
                updateUi();
                await pullers[0].fetchData(false);
            };
            syncAllTickets();
        }
    }, [context.shouldPushTickets]);

    useEffect(() => {
        if (context.shouldSync) {
            getChanges();
        }
    }, [context.shouldSync]);

    useEffect(() => {
        const statusCounts = pullers.reduce(
            (result, puller) => {
                result[puller.status]++;
                return result;
            },
            [0, 0, 0, 0]
        );

        const isLoading = statusCounts[SyncStatus.Loading] > 0;
        const isStale = statusCounts[SyncStatus.Stale] > 0;
        const hasErrors = statusCounts[SyncStatus.Error] > 0;

        if (!isLoading) {
            if (hasErrors) {
                setStatus(SyncStatus.Error);
                props.setStatus(SyncStatus.Error);
            } else if (isStale) {
                setStatus(SyncStatus.Stale);
                props.setStatus(SyncStatus.Stale);
            } else {
                setStatus(SyncStatus.Fresh);
                props.setStatus(SyncStatus.Fresh);
            }
        }

        setDoUiUpdate(false);
    }, [doUiUpdate]);

    // Clear shouldPushTickets when push completes so a second push cannot be triggered until user closes dialog.
    useEffect(() => {
        if (showForm === FormType.push && pullers[0].status !== SyncStatus.Loading) {
            context.setShouldPushTickets(false);
        }
    }, [showForm, pullers[0].status]);

    useEffect(() => {
        let cancelled = false;

        const pollForNewDay = (firstCall = false) => {
            if (cancelled) return;

            const activationTime = dayjs();
            const previousActivationTimeString = localStorage.getItem(DATE_CHANGE_TRACKER);
            const previousActivationTime = HasValue(previousActivationTimeString) ? dayjs(previousActivationTimeString) : activationTime.subtract(2, "days");

            if (firstCall || activationTime.format(DATE_FORMAT) !== previousActivationTime.format(DATE_FORMAT)) {
                localStorage.setItem(DATE_CHANGE_TRACKER, activationTime.toISOString());
                getChanges(firstCall);
                void deleteTicketsPastRetention(TicketDatabase.ticket);
            }

            if (!cancelled) {
                pollingTimeoutRef.current = setTimeout(() => pollForNewDay(false), 5 * 1000);
            }
        };

        pollForNewDay(true);

        return () => {
            cancelled = true;
            if (pollingTimeoutRef.current !== null) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
            }
        };
    }, [useEffectAnchor]);

    useEffect(() => {
        return () => {
            getChangesTimeoutsRef.current.forEach((id) => clearTimeout(id));
            getChangesTimeoutsRef.current = [];
        };
    }, []);

    const getChanges = async (firstCall = false) => {
        getChangesTimeoutsRef.current.forEach((id) => clearTimeout(id));
        getChangesTimeoutsRef.current = [];

        setStatus(SyncStatus.Loading);
        props.setStatus(SyncStatus.Loading);
        await ResetSyncCounts();
        setShowForm(FormType.sync);
        updateUi();

        pullers.forEach((puller, index) => {
            const id = setTimeout(() => {
                puller.fetchData(firstCall);
            }, index * 50);
            getChangesTimeoutsRef.current.push(id);
        });
    };

    function handleClose() {
        const statusCounts = pullers.reduce(
            (result, puller) => {
                result[puller.status]++;
                return result;
            },
            [0, 0, 0, 0]
        );

        const isLoading = statusCounts[SyncStatus.Loading] > 0;
        const isStale = statusCounts[SyncStatus.Stale] > 0;
        const hasErrors = statusCounts[SyncStatus.Error] > 0;

        if (!isLoading) {
            if (hasErrors) {
                setStatus(SyncStatus.Error);
                props.setStatus(SyncStatus.Error);
            } else if (isStale) {
                setStatus(SyncStatus.Stale);
                props.setStatus(SyncStatus.Stale);
            } else {
                setStatus(SyncStatus.Fresh);
                props.setStatus(SyncStatus.Fresh);
            }
        }

        context.setShouldPushTickets(false);
        context.setShouldSync(false);
        setShowForm(FormType.none);
    }

    if (showForm === FormType.none) {
        return <></>;
    }

    if (showForm === FormType.push) {
        return (
            <Dialog open>
                <Grid container sx={{ p: 6 }} flexDirection={"column"} alignItems={"center"} justifyContent={"center"}>
                    <Grid item sx={{ pt: 2 }}>
                        <Typography color={"primary"}>SYNCING TICKET TO RPS</Typography>
                    </Grid>
                    <Grid item sx={{ pt: 2 }}>
                        {pullers[0].status === SyncStatus.Loading && <ProgressIcon key={`dot0`} color={"primary"} />}
                        {pullers[0].status === SyncStatus.Fresh && <SuccessIcon key={`dot0`} color={"success"} />}
                        {pullers[0].status === SyncStatus.Stale && <WarningIcon key={`dot0`} color={"warning"} />}
                        {pullers[0].status === SyncStatus.Error && <ErrorIcon key={`dot0`} color={"error"} />}
                    </Grid>
                    <Grid item sx={{ pt: 2 }}>
                        {(pullers[0].status === SyncStatus.Loading || pullers[0].status === SyncStatus.Fresh) && (
                            <Typography color={"primary"}>{pullers[0].status === SyncStatus.Loading ? "..." : "SYNC COMPLETE"}</Typography>
                        )}
                        {pullers[0].status !== SyncStatus.Loading && pullers[0].status !== SyncStatus.Fresh && (
                            <Typography color={theme.palette.warning.main}>SYNC DID NOT COMPLETE, TRY AGAIN LATER</Typography>
                        )}
                    </Grid>
                    <Grid item sx={{ pt: 2 }}>
                        {pullers[0].status === SyncStatus.Loading ? (
                            <CircularProgress color={"primary"} />
                        ) : (
                            <Button sx={{ mt: 2 }} variant={"contained"} onClick={handleClose}>
                                OK
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Dialog>
        );
    }

    return (
        <Dialog open>
            <Grid container sx={{ p: 6, pb: 4 }} flexDirection={"column"} alignItems={"center"} justifyContent={"center"}>
                <Grid item>
                    <Typography color={"primary"}>
                        {pullers.reduce((result, puller) => {
                            return result || puller.status === SyncStatus.Loading;
                        }, false)
                            ? "LOADING DATA FROM RPS"
                            : "DATA LOADED FROM RPS"}
                    </Typography>
                </Grid>
                <Grid item sx={{ pt: 2 }}>
                    {pullers.map((puller, index) => {
                        switch (puller.status) {
                            case SyncStatus.Loading:
                                return <ProgressIcon key={`dot${index}`} color={"primary"} />;
                            case SyncStatus.Fresh:
                                return <SuccessIcon key={`dot${index}`} color={"success"} />;
                            case SyncStatus.Stale:
                                return <WarningIcon key={`dot${index}`} color={"warning"} />;
                            case SyncStatus.Error:
                                return <ErrorIcon key={`dot${index}`} color={"error"} />;
                        }

                        return <></>;
                    })}
                </Grid>
                <Grid item sx={{ pt: 2 }}>
                    {pullers.reduce((result, puller) => {
                        return result || puller.status === SyncStatus.Loading;
                    }, false) ? (
                        <CircularProgress color={"primary"} />
                    ) : (
                        <Button sx={{ mt: 2 }} variant={"contained"} onClick={handleClose}>
                            OK
                        </Button>
                    )}
                </Grid>
                <Grid item sx={{ pt: 4 }}>
                    <Typography color={"primary"}>{APPLICATION_VERSION}</Typography>
                </Grid>
            </Grid>
        </Dialog>
    );
}
